import * as angular from 'angular';
import { States } from "../../shared/config/constant.module";
import template from './plantPerformanceCharts.tpl.html';
import AuthDataServiceName from "../../services/auth/authData.service";
import * as Highcharts from 'highcharts/highstock';
import HC_more from 'highcharts/highcharts-more';
HC_more(Highcharts);
import solidGauge from 'highcharts/modules/solid-gauge';
solidGauge(Highcharts);
import patternFill from "highcharts/modules/pattern-fill";
patternFill(Highcharts);
import { Plant, NavigationModel } from '../../models/navigation';
import { PlantLocation, LocationsService } from '../../services/locations/locations.service';
import { NavigationService } from '../../services/navigation/navigation.service';
import { LoaderService } from '../../components/loader/loader.service';
import PlantPerformanceServiceName, { PlantPerformanceService } from '../../services/dashboard/plantPerformance.service';
import * as _ from "lodash";
import { apsEnums } from '../../shared/dto/aps.enums';
import { StateParams } from "../../common";
import * as moment from 'moment';
import { apsCommon } from '../../shared/dto/aps.common';
import { apsViewModels } from "../../shared/dto/APS.ViewModels";
import ChartConfigServiceName, { ChartConfigService } from '../../services/dashboard/chartConfig.service';

let requires = [AuthDataServiceName, PlantPerformanceServiceName, ChartConfigServiceName];
let shortPercentType: string;
let longPercentType: string;
let red = '#f04124';
let green = '#358753';

function plantPerformanceChartConfig($stateProvider: angular.ui.IStateProvider) {
  $stateProvider.state({
    name: States.PLANT_PERFORMANCE_CHARTS,
    url: '/plant-peformance-charts',
    params: {
      'selectedLocation': ''
    },
    controller: 'PlantPerformanceChartsCtrl as vm',
    template: template,
    data: { pageTitle: 'Company Performance Charts' }
  });
}

// Generic function that is used in the directive for each chart on the page
function genericChart() {
  return {
    restrict: 'E',
    template: '<div></div>',
    scope: {
      options: '='
    },
    link: function (scope, element) {
      // Create the highchart      
      var chart = Highcharts.chart(element[0], scope.options);

      // watch for the options to change and update chart as necessary
      scope.$watch('options', function (newVal) {
        if (newVal) {
          chart.update(scope.options);
        }
      }, true);
    }
  };
}

class PlantPerformanceChartsCtrl {
  plants: Plant[];
  selectedPlant: Plant;
  monthlyTopFive: apsViewModels.PackProductItem[];
  monthlyBottomFive: apsViewModels.PackProductItem[];
  yearlyTopFive: apsViewModels.PackProductItem[];
  yearlyBottomFive: apsViewModels.PackProductItem[];
  locations: PlantLocation[];
  selectedLocation: PlantLocation;
  months: apsCommon.IdAndName[];
  selectedMonth: apsCommon.IdAndName;
  chartConfigService: ChartConfigService;
  dailyChartConfig: any;
  monthlyChartConfig: any;
  ytdChartConfig: any;
  monthlyFurnaceChartConfig: any;
  dailyFurnaceChartConfig: any;
  ytdFurnaceChartConfig: any;
  dailyLineHoldsChartConfig: any;
  isTank: boolean = false;
  isShop: boolean = false;
  showHelpWindow: boolean = false;
  helpText: string = "";

  locationsService: LocationsService;
  plantPerformanceService: PlantPerformanceService;

  selectedMonthName: string;
  selectedYear: string;
  selectedPreviousYear: string;

  constructor(
    locationsService: LocationsService,
    navigationService: NavigationService,
    loaderService: LoaderService,
    plantPerformanceService: PlantPerformanceService,
    $stateParams: StateParams,
    chartConfigService: ChartConfigService) {

    this.locationsService = locationsService;
    this.plantPerformanceService = plantPerformanceService;
    this.chartConfigService = chartConfigService;

    Highcharts.setOptions({
      lang: {
        thousandsSep: ','
      }
    });

    var promise = locationsService.initialize().then(() => {
      return navigationService.getNavigation().then((nav: NavigationModel) => {
        this.plants = nav.plants();
        this.selectedPlant = nav.selectedPlant;
        this.updatePlant($stateParams['selectedLocation']);
      });
    });

    loaderService.loadingPromise(promise);
    this.setMonthInfo();

    // Get the default configuration for each chart from the service
    this.dailyChartConfig = chartConfigService.getDailyPackChartConfig();
    this.monthlyChartConfig = chartConfigService.getMonthlyPackChartConfig();
    this.ytdChartConfig = chartConfigService.getYearlyPackChartConfig();
    this.dailyFurnaceChartConfig = chartConfigService.getDailyFurnaceChartConfig();
    this.monthlyFurnaceChartConfig = chartConfigService.getMonthlyFurnaceChartConfig();
    this.ytdFurnaceChartConfig = chartConfigService.getYearlyFurnaceChartConfig();
    this.dailyLineHoldsChartConfig = chartConfigService.getDailyLineHoldsChartConfig();
  }

  showHelp(chartType: string) {
    if (this.showHelpWindow) { this.showHelpWindow = false; }
    this.helpText = this.chartConfigService.getHelpText(chartType, this.isShop);
    this.toggleHelpWindow();
  }

  toggleHelpWindow() {
    this.showHelpWindow = !this.showHelpWindow;
  }

  getMonthList(maxDate: string): apsCommon.IdAndName[] {
    let startDate = moment(maxDate);
    let months: apsCommon.IdAndName[] = [];

    for (let i = 0; i < 12; i++) {
      months.push({ id: (startDate.format("MM/01/YYYY")), name: startDate.format("MMMM YYYY") });
      startDate.add("M", -1);
    }

    return months;
  }

  updatePlant(selectedLocationString = null): void {
    this.locations = _.filter(
      this.locationsService.getPlantLocations(
        this.selectedPlant),
      locations => locations.locationType !== apsEnums.LocationType.Other);

    this.selectedLocation = _.find(this.locations, item => item.name === "Plant");

    if (selectedLocationString != null && selectedLocationString !== "") {
      this.selectedLocation = _.find(this.locations, location => location.name === selectedLocationString);
    }

    this.loadCharts();
  }

  updateLocation(): void {
    this.loadCharts();
  }

  updateMonth(): void {
    this.loadCharts();
  }

  setMonthInfo() {
    if (this.months == null) {
      this.months = this.getMonthList(moment().date() === 1 ? moment().add(-1, 'M').format("YYYYMMDD") : moment().format("YYYYMMDD"));
      this.selectedMonth = _.head(this.months);
    }
  }

  loadCharts(): void {
    this.isTank = false;
    this.isShop = false;

    this.selectedMonthName = moment(this.selectedMonth.id).format("MMM").toUpperCase();
    this.selectedYear = moment(this.selectedMonth.id).format("YYYY");
    this.selectedPreviousYear = (moment(this.selectedMonth.id).year() - 1).toString();

    if (this.selectedLocation.locationType === apsEnums.LocationType.Shop) {
      this.isShop = true;
      shortPercentType = "PTP";
      longPercentType = "Pack To Pull";
    } else {
      if (this.selectedLocation.locationType === apsEnums.LocationType.Tank) {
        this.isTank = true;
      }
      shortPercentType = "PTM";
      longPercentType = "Pack To Melt";
    }

    this.loadPackChart();
    this.loadPackProducts();

    if (this.isTank) {
      this.loadFurnaceCharts();
    }

    if (this.isShop) {
      this.loadShopCharts();
    }
  }

  loadPackChart() {
    // This will load percent pack charts if a shop is selected, otherwise pack to melt charts (for plant/tank)
    this.plantPerformanceService.getPackChartData(
      this.selectedLocation, this.selectedMonth == null ? moment().month().toString() : this.selectedMonth.id).then(data => {

        // Daily chart      
        this.dailyChartConfig.xAxis.categories = data.currentYearDaily.map(
          x => moment(x.startDate).format("MM/DD")); // x axis categories - day
        this.dailyChartConfig.series[0].data = data.currentYearDaily.map(x => x.percent); // current year column
        this.dailyChartConfig.series[1].data = data.previousYearDaily.map(x => x.percent); // previous year column     
        this.dailyChartConfig.yAxis.plotLines[0].value = data.aopDay; // plot line for the AOP target value
        this.dailyChartConfig.yAxis.plotLines[0].label.text = this.selectedMonthName + ' ' + this.selectedYear
          + " AOP (" + this.dailyChartConfig.yAxis.plotLines[0].value.toFixed(1) + "%)"; // plot line text
        this.dailyChartConfig.title.text = '<b>' + this.selectedMonthName + ' '
          + this.selectedYear + ' ' + shortPercentType + '</b>'; // Chart title (varies if percent pack/pack to melt)
        this.dailyChartConfig.series[0].name =
          this.selectedYear + ' ' + longPercentType; // column definition - current year (varies if percent pack/pack to melt)
        this.dailyChartConfig.series[1].name =
          this.selectedPreviousYear + ' ' + longPercentType; // column definition - previous year (varies if percent pack/pack to melt)

        // Monthly chart  
        this.monthlyChartConfig.xAxis.categories = data.currentYearMonthly.map(
          x => moment(x.startDate).format("MM/YYYY"));  // x axis categories - month
        this.monthlyChartConfig.series[0].data = data.currentYearMonthly.map(x => x.percent); // current year column
        this.monthlyChartConfig.series[1].data = data.aopMonthly.map(x => x.percent); // aop column
        this.monthlyChartConfig.series[2].data = data.previousYearMonthly.map(x => x.percent); // previous year column            
        this.monthlyChartConfig.series[3].data = data.currentYearMonthly.map(
          x => ((x.percent / _.find(data.aopMonthly, y => y.startDate === x.startDate).percent) * 100)); // % of AOP spline
        this.monthlyChartConfig.title.text = '<b>' + this.selectedYear + ' '
          + shortPercentType + '</b>'; // Chart title (varies if percent pack/pack to melt)
        this.monthlyChartConfig.series[0].name = this.selectedYear + ' '
          + longPercentType; // column definition - current year (varies if percent pack/pack to melt)
        this.monthlyChartConfig.series[1].name = this.selectedYear + ' AOP';
        this.monthlyChartConfig.series[2].name = this.selectedPreviousYear
          + ' ' + longPercentType; // column definition - previous year (varies if percent pack/pack to melt)

        // YTD gauge chart  
        this.ytdChartConfig.series[0].data[0] = data.ytd; // ytd % (percent pack or pack to melt)  
        this.ytdChartConfig.yAxis.stops = this.getPackGaugeColor(data.ytd, data.aopYear); // set the gauge color based on % of AOP
        this.ytdChartConfig.yAxis.plotLines[0].value = data.aopYear; // plot line for AOP target value
        this.ytdChartConfig.yAxis.plotLines[0].label.text = this.selectedYear + " AOP (" + data.aopYear.toFixed(1) + "%)"; // plot line text
        this.ytdChartConfig.series[0].dataLabels.format = // data label text (varies if percent pack/pack to melt)
          '<div style="text-align:center;">' +
          '<span style="font-size:10px;">' + this.selectedYear + ' ' + shortPercentType + '</span><br/>' +
          '<span style="font-size:10px">' + data.ytd.toFixed(1) + '%</span><br/>' +
          '</div>';
      });
  }

  loadPackProducts() {
    this.plantPerformanceService.getPackProductsInfo(this.selectedLocation, this.selectedMonth.id).then(data => {

      // set top five/bottom five product info
      this.monthlyTopFive = data.monthlyTopFivePackProducts;
      this.monthlyBottomFive = data.monthlyBottomFivePackProducts;
      this.yearlyTopFive = data.yearlyTopFivePackProducts;
      this.yearlyBottomFive = data.yearlyBottomFivePackProducts;
    });
  }

  loadFurnaceCharts() {
    // Only shows when a tank is selected
    this.plantPerformanceService.getFurnaceChartData(this.selectedLocation, this.selectedMonth.id).then(data => {

      // Daily chart
      this.dailyFurnaceChartConfig.xAxis.categories = data.furnaceDailyDataItems.map(
        x => moment(x.startDate).format("MM/DD")); // x axis - day     
      if (data.furnaceCapacityItems != null && data.furnaceCapacityItems.length > 0) {
        this.dailyFurnaceChartConfig.yAxis[0].max = _.maxBy(
          data.furnaceCapacityItems, x => x.nominal).nominal + 100; 
          // set y axis range max to be 100 more than the nominal value, to make the spline look better
      }
      this.dailyFurnaceChartConfig.series[0].data = data.furnaceDailyDataItems.map(x => x.tonsPulled); 
      // Tons pulled column which is actually Tons Melted    
      
      this.dailyFurnaceChartConfig.series[1].data = data.furnaceDailyDataItems.map(x => x.tonsPacked); // Tons packed column
      this.dailyFurnaceChartConfig.series[2].data = data.furnaceDailyDataItems.map(
        x => ((x.tonsPulled /
          (this.getNominalCapacityForDate(data.furnaceCapacityItems, x.startDate) * x.days))
          * 100));

      // Furnace Utilization spline
      this.dailyFurnaceChartConfig.title.text = '<b>' + this.selectedMonthName 
        + ' ' + this.selectedYear + ' Furnace Utilization</b><br/>' 
        + this.getCapacityTextForMonth(data.furnaceCapacityItems); // Chart title, with nominal/environmental values displayed below
      this.dailyFurnaceChartConfig.series[0].name = 'Tons Melted'; // column definition
      this.dailyFurnaceChartConfig.series[1].name = 'Tons Packed'; // column definition

      // Monthly chart     
      this.monthlyFurnaceChartConfig.xAxis.categories = data.furnaceMonthlyDataItems.map(
        x => moment(x.startDate).format("MM/YYYY")); // x axis - month
      if (data.furnaceCapacityItems != null && data.furnaceCapacityItems.length > 0) {
        this.monthlyFurnaceChartConfig.yAxis[0].max = _.maxBy(
          data.furnaceCapacityItems, x => x.nominal).nominal * 31;  
          // set y axis range max to be the nominal * 31 days, to make the spline look better
      }
      this.monthlyFurnaceChartConfig.series[0].data = data.furnaceMonthlyDataItems.map(x => x.tonsPulled); // Tons pulled column
      this.monthlyFurnaceChartConfig.series[1].data = data.furnaceMonthlyDataItems.map(x => x.tonsPacked); // Tons packed column  
      this.monthlyFurnaceChartConfig.series[2].data = data.furnaceMonthlyDataItems.map(
        x => ((x.tonsPulled /
          (this.getNominalCapacityForDate(data.furnaceCapacityItems, x.startDate) * x.days))
          * 100)); // Furnace Utilization spline    
      this.monthlyFurnaceChartConfig.title.text = '<b>' + this.selectedYear 
        + ' Furnace Utilization</b><br/>' 
        + this.getCapacityText(data.furnaceCapacityItems); // Chart title, with nominal/environmental values displayed below
      this.monthlyFurnaceChartConfig.series[0].name = 'Tons Melted'; // column definition
      this.monthlyFurnaceChartConfig.series[1].name = 'Tons Packed'; // column definition   

      // YTD Furnace Utilization gauge chart
      this.ytdFurnaceChartConfig.series[0].data[0] = data.furnaceYTDUtilization; // furnace utilization % for the year
      this.ytdFurnaceChartConfig.yAxis.stops = this.getFurnaceGaugeColor(data.furnaceYTDUtilization); 
      // set the gauge color based on pre-determined ranges
      
      this.ytdFurnaceChartConfig.series[0].dataLabels.format = // data label text 
        '<div style="text-align:center;">' +
        '<span style="font-size:10px;">' + this.selectedYear + '</span><br/>' +
        '<span style="font-size:10px">Furnace Util.</span><br/>' +
        '<span style="font-size:10px">' + data.furnaceYTDUtilization.toFixed(1) + '%</span><br/>' +
        '</div>';
    });
  }

  loadShopCharts() {
    // Only chart specific to shops is the Line Item Holds
    this.plantPerformanceService.getLineItemHoldsData(this.selectedLocation, this.selectedMonth.id).then(data => {

      // set daily chart x axis days
      this.dailyLineHoldsChartConfig.xAxis.categories = data.map(x => moment(x.startDate).format("MM/DD"));

      // set daily chart current year data
      this.dailyLineHoldsChartConfig.series[0].data = data.map(x => (x.lineHolds / x.gross) * 100);

      // Set the chart text
      this.dailyLineHoldsChartConfig.title.text = '<b>' + this.selectedMonthName + ' ' + this.selectedYear + ' % Line Holds</b>';
    });
  }

  getCapacityTextForMonth(capacityItems: apsViewModels.TankLimitItem[]) {
    let month = new Date(this.selectedMonth.id);

    // Find the capacities that are valid for the selected month
    let capacityMatches = capacityItems.filter(x => (x.startDate == null || moment(x.startDate).isBefore(month)) &&
      (x.endDate == null || moment(x.endDate).isAfter(month)));

    return this.getCapacityText(capacityMatches);
  }

  getCapacityText(capacityItems: apsViewModels.TankLimitItem[]) {
    // format the capacity text that displays underneath furnace utilization charts
    let capacityText = '<span class="capacityText">';
    if (capacityItems.length === 1) {
      capacityText += "Nominal Capacity: " + capacityItems[0].nominal + "&nbsp;&nbsp;&nbsp;";
      capacityText += "Environmental Capacity: " + capacityItems[0].environmental;
    } else {
      capacityItems.forEach(x => {
        let startDate = x.startDate == null ? 'Unknown' : moment(x.startDate).utc().format("MM/DD/YYYY");
        let endDate = x.endDate == null ? 'Current' : moment(x.endDate).utc().format("MM/DD/YYYY");
        capacityText += startDate + " - " + endDate 
          + "&nbsp;&nbsp;&nbsp;" + "Nominal Capacity: " 
          + x.nominal + "&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;" +
          "Environmental Capacity: " + x.environmental + '</br>';
      });
    }

    capacityText += '</span>';
    return capacityText;
  }

  getNominalCapacityForDate(furnaceCapacityItems: apsViewModels.TankLimitItem[], selectedDate: string): number {
    // find the tank limit that would apply to the date in question
    let capacityItem = _.find(furnaceCapacityItems, y =>
      (y.startDate == null || moment(selectedDate).isSameOrAfter(moment(y.startDate).utc().format("MM/DD/YYYY")))
      && (y.endDate == null || moment(selectedDate).isSameOrBefore(moment(y.endDate).utc().format("MM/DD/YYYY"))));
    return capacityItem == null ? 0 : capacityItem.nominal;
  }

  getPackGaugeColor(packPercent: number, aop: number) {
    let value = (packPercent / aop) * 100;
    switch (true) {
      case (value >= 100):
        return [
          [0, green],
          [1, green]
        ];
      default:
        return [
          [0, red],
          [1, red]
        ];
    }
  }

  getFurnaceGaugeColor(percent: number) {
    switch (true) {
      case (percent >= 95):
        return [
          [0, green],
          [1, green]
        ];
      default:
        return [
          [0, red],
          [1, red]
        ];
    }
  }
}

export default angular.module('aps.controller.plantPerformanceCharts', requires)
  .config(plantPerformanceChartConfig)
  .controller('PlantPerformanceChartsCtrl', PlantPerformanceChartsCtrl)
  .directive('dailyChart', genericChart)
  .directive('monthlyChart', genericChart)
  .directive('ytdChart', genericChart)
  .directive('dailyFurnaceChart', genericChart)
  .directive('monthlyFurnaceChart', genericChart)
  .directive('ytdFurnaceChart', genericChart)
  .directive('dailyLineholdsChart', genericChart)
  .name;



// WEBPACK FOOTER //
// ./src/app/controllers/dashboard/plantPerformanceCharts.controller.ts